import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractDraftingService from '../../app/services/hooks/useContractDraftingService';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { AppContext } from '../../contexts/AppContext';

interface ContractType {
    id: number;
    name: string;
    price: number;
}

interface SignatureInvitation {
    id: number;
    signer_name: string;
    signer_email: string;
    status: string;
    signed_at: string | null;
}

interface RequestDetail {
    id: number;
    reference_code: string;
    contract_type: ContractType;
    preview_document_url: string | null;
    final_document_url: string | null;
    payment_amount: number;
    status: string;
    payment_status: string;
    created_at: string;
    can_skip_payment?: boolean;
    has_active_membership?: boolean;
    is_free_contract?: boolean;
    is_admin?: boolean;
    signatures_required: number;
    signatures_completed: number;
    signature_status: string;
    signature_invitations: SignatureInvitation[];
}

const ContractPreview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { getRequestDetail, generatePreview, createPayment, fetchingRequestDetail, fetchingGeneratePreview, fetchingCreatePayment } = useContractDraftingService();
    const { requestConfirmation } = useReactConfirmAlert();
    
    const [request, setRequest] = useState<RequestDetail | null>(null);
    const [previewGenerated, setPreviewGenerated] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    useEffect(() => {
        loadRequestDetail();
    }, []);

    useEffect(() => {
        if (fetchingRequestDetail || fetchingGeneratePreview || fetchingCreatePayment) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingRequestDetail, fetchingGeneratePreview, fetchingCreatePayment]);

    const loadRequestDetail = () => {
        getRequestDetail(parseInt(id), {
            onSuccess: (res) => {
                const data = res.data.request;
                setRequest(data);
                setPreviewGenerated(data.status === 'preview_generated' || data.status === 'pending_payment' || data.status === 'paid' || data.status === 'completed');
                setIsCompleted(data.status === 'paid' || data.status === 'completed' || data.payment_status === 'paid');
            },
            onError: (res) => {
                toast.error(res.message || 'Error al cargar los datos');
                history.push('/intranet/contracts');
            }
        });
    };

    const handleGeneratePreview = () => {
        generatePreview(parseInt(id), {
            onSuccess: () => {
                toast.success('Vista previa generada exitosamente');
                setPreviewGenerated(true);
                loadRequestDetail();
            },
            onError: (res) => {
                toast.error(res.message || 'Error al generar la vista previa');
            }
        });
    };

    const handleProceedToPayment = () => {
        // Si es admin o tiene plan, no mostrar confirmación de pago
        if (request?.can_skip_payment) {
            createPayment(parseInt(id), {
                onSuccess: (res) => {
                    if (res.data.is_admin_free) {
                        toast.success('¡Documento generado! Como administrador, no requiere pago.');
                    } else if (res.data.is_free_with_membership) {
                        toast.success(`¡Documento activado con tu membresía! Contratos restantes: ${res.data.membership?.contracts_remaining}`);
                    } else if (res.data.is_free_contract) {
                        toast.success('¡Documento final generado! Este tipo de contrato no tiene costo.');
                    } else {
                        toast.success('¡Documento final generado!');
                    }
                    // Descargar automáticamente el documento final recién generado
                    if (res.data.download_url) {
                        window.open(res.data.download_url, '_blank');
                    }
                    loadRequestDetail();
                },
                onError: (res) => {
                    toast.error(res.message || 'Error al generar el documento');
                }
            });
            return;
        }
        
        // Si no tiene plan, mostrar confirmación de pago
        requestConfirmation({
            title: '¿Proceder con el pago?',
            message: `Se procesará un pago de $${request?.payment_amount?.toLocaleString('es-CL')} CLP. Después del pago podrás descargar el documento final.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, proceder al pago',
                    onClick: () => {
                        createPayment(parseInt(id), {
                            onSuccess: (res) => {
                                const paymentUrl = res.data.payment_url;
                                if (paymentUrl) {
                                    window.location.href = paymentUrl;
                                } else {
                                    toast.error('No se pudo generar la URL de pago');
                                }
                            },
                            onError: (res) => {
                                toast.error(res.message || 'Error al crear la orden de pago');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    const handleDownloadPreview = () => {
        if (request?.preview_document_url) {
            window.open(request.preview_document_url, '_blank');
        }
    };

    const handleDownloadFinal = () => {
        // Si ya tenemos la URL del documento final, descargar directo
        if (request?.final_document_url) {
            window.open(request.final_document_url, '_blank');
            return;
        }
        // El pago está registrado pero el documento no se generó (intento previo
        // fallido). El endpoint es idempotente: regenera y devuelve la descarga.
        createPayment(parseInt(id), {
            onSuccess: (res) => {
                if (res.data.download_url) {
                    window.open(res.data.download_url, '_blank');
                } else {
                    toast.error('El documento aún no está disponible. Intenta nuevamente en unos segundos.');
                }
                loadRequestDetail();
            },
            onError: (res) => {
                toast.error(res.message || 'No se pudo obtener el documento final');
            }
        });
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="mb-4">
                                <h4 className="mb-1">👁️ Vista Previa del Contrato</h4>
                                <p className="text-muted mb-0">{request?.contract_type?.name || ''} - {request?.reference_code || ''}</p>
                            </div>

                            {/* Información del contrato */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="text-muted mb-3">Información del Contrato</h6>
                                            <div className="mb-2">
                                                <small className="text-muted">Código de Referencia</small>
                                                <p className="mb-0 fw-bold">{request?.reference_code || ''}</p>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Tipo de Contrato</small>
                                                <p className="mb-0">{request?.contract_type?.name || ''}</p>
                                            </div>
                                            <div className="mb-0">
                                                <small className="text-muted">Fecha de Creación</small>
                                                <p className="mb-0">{request?.created_at ? new Date(request.created_at).toLocaleDateString('es-CL') : ''}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    {request?.can_skip_payment ? (
                                        <div className="card border-0" style={{ background: 'linear-gradient(135deg, #001849 0%, #003380 100%)' }}>
                                            <div className="card-body text-center text-white">
                                                <h6 className="mb-3 text-white" style={{ opacity: 0.9 }}>
                                                    {request.is_admin
                                                        ? '👑 Administrador'
                                                        : request.is_free_contract ? '🎁 Sin costo' : '⭐ Plan Activo'
                                                    }
                                                </h6>
                                                <h2 className="mb-1 text-white" style={{ fontWeight: 700 }}>GRATIS</h2>
                                                <small className="text-white" style={{ opacity: 0.85 }}>
                                                    {request.is_admin 
                                                        ? 'Sin costo para administradores' 
                                                        : request.is_free_contract
                                                            ? 'Este tipo de contrato es gratuito'
                                                            : 'Incluido en tu membresía'
                                                    }
                                                </small>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="card border-0" style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' }}>
                                            <div className="card-body text-center text-white">
                                                <h6 className="mb-3 text-white" style={{ opacity: 0.9 }}>Precio del Servicio</h6>
                                                <h2 className="mb-1 text-white" style={{ fontWeight: 700 }}>${request?.payment_amount?.toLocaleString('es-CL') || '0'} CLP</h2>
                                                <small className="text-white" style={{ opacity: 0.85 }}>Versión final del documento</small>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Vista previa */}
                            {!previewGenerated ? (
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>Vista Previa No Generada</strong>
                                    <p className="mb-3 mt-2">
                                        Genera la vista previa del contrato para verificar que todo esté correcto antes de proceder.
                                    </p>
                                    <ButtonGeneral
                                        type="button"
                                        variant="primary"
                                        title="Generar Vista Previa"
                                        onClick={handleGeneratePreview}
                                        loading={fetchingGeneratePreview}
                                        icon="fa-eye"
                                    />
                                </div>
                            ) : isCompleted ? (
                                // Contrato completado - mostrar documento final y opción de firmas
                                <div>
                                    <div className="alert alert-success">
                                        <i className="fas fa-check-circle me-2"></i>
                                        <strong>¡Documento Listo!</strong>
                                        <p className="mb-0 mt-2">
                                            Tu contrato está listo para descargar. 
                                            {(request?.signatures_required ?? 0) > 0 && ' Puedes invitar a los participantes a firmar.'}
                                        </p>
                                    </div>

                                    <div className="card mb-4">
                                        <div className="card-body text-center py-5">
                                            <i className="fas fa-file-pdf" style={{ fontSize: '64px', color: '#28a745' }}></i>
                                            <h5 className="mt-3 mb-3">Documento Final</h5>
                                            <p className="text-muted mb-4">
                                                Descarga tu contrato en su versión final
                                            </p>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <ButtonGeneral
                                                    type="button"
                                                    variant="primary"
                                                    title="Descargar Documento"
                                                    onClick={handleDownloadFinal}
                                                    loading={fetchingCreatePayment}
                                                    icon="fa-download"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sección de Firmas - Siempre visible para contratos pagados */}
                                    <div className="card mb-4" style={{ borderLeft: '4px solid #001849' }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">
                                                    <i className="fas fa-signature me-2" style={{ color: '#001849' }}></i>
                                                    Firmas del Contrato
                                                </h6>
                                                {(request?.signatures_required ?? 0) > 0 && (
                                                    <span className="badge" style={{ background: '#001849' }}>
                                                        {request?.signatures_completed ?? 0}/{request?.signatures_required ?? 0} firmado
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {request?.signature_invitations && request.signature_invitations.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Nombre</th>
                                                                <th>Email</th>
                                                                <th>Estado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {request?.signature_invitations.map((inv) => (
                                                                <tr key={inv.id}>
                                                                    <td>{inv.signer_name}</td>
                                                                    <td>{inv.signer_email}</td>
                                                                    <td>
                                                                        <span className={`badge bg-${inv.status === 'signed' ? 'success' : inv.status === 'pending' ? 'warning' : 'secondary'}`}>
                                                                            {inv.status === 'signed' ? 'Firmado' : inv.status === 'pending' ? 'Pendiente' : inv.status}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p className="text-muted mb-3">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    Puedes invitar a las partes del contrato para que lo firmen digitalmente.
                                                </p>
                                            )}
                                            
                                            <ButtonGeneral
                                                type="button"
                                                variant="primary"
                                                title="Gestionar Firmantes"
                                                onClick={() => history.push(`/intranet/contracts/${id}/payment-success`)}
                                                icon="fa-user-plus"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Preview generado pero no completado
                                <div>
                                    <div className="alert alert-success">
                                        <i className="fas fa-check-circle me-2"></i>
                                        <strong>Vista Previa Generada</strong>
                                        <p className="mb-0 mt-2">
                                            Descarga y revisa el documento antes de continuar.
                                            {request?.can_skip_payment
                                                ? request.is_admin
                                                    ? ' Como administrador, no necesitas pagar.'
                                                    : request.is_free_contract
                                                        ? ' Este tipo de contrato es gratuito, no necesitas pagar.'
                                                        : ' Como usuario con plan, no necesitas pagar.'
                                                : ' Si necesitas hacer cambios, edita los datos y regenera el preview.'
                                            }
                                        </p>
                                    </div>

                                    <div className="card mb-4">
                                        <div className="card-body text-center py-5">
                                            <i className="fas fa-file-word" style={{ fontSize: '64px', color: '#667eea' }}></i>
                                            <h5 className="mt-3 mb-3">Vista Previa Disponible</h5>
                                            <p className="text-muted mb-4">
                                                Descarga y revisa el documento antes de continuar
                                            </p>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <ButtonGeneral
                                                    type="button"
                                                    variant="outline"
                                                    title="Descargar Vista Previa"
                                                    onClick={handleDownloadPreview}
                                                    icon="fa-download"
                                                />
                                                <ButtonGeneral
                                                    type="button"
                                                    variant="secondary"
                                                    title="Regenerar Preview"
                                                    onClick={handleGeneratePreview}
                                                    loading={fetchingGeneratePreview}
                                                    icon="fa-sync-alt"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-body">
                                            <h6 className="mb-3"><i className="fas fa-info-circle me-2" />Nota importante</h6>
                                            <p className="mb-0">
                                                {request?.can_skip_payment 
                                                    ? 'Una vez que obtengas el documento final, podrás invitar a los participantes a firmar.'
                                                    : 'Una vez realizado el pago, el documento estará disponible para invitar a firmar a los participantes.'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-between mt-4">
                                <ButtonGeneral
                                    type="button"
                                    variant="outline"
                                    title="Volver"
                                    onClick={() => history.push('/intranet/contracts')}
                                />
                                <div className="d-flex gap-2">
                                    {!isCompleted && (
                                        <ButtonGeneral
                                            type="button"
                                            variant="secondary"
                                            title="Editar Datos"
                                            onClick={() => history.push(`/intranet/contracts/${id}/form`)}
                                            icon="fa-edit"
                                        />
                                    )}
                                    {previewGenerated && !isCompleted && (
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            title={request?.can_skip_payment ? 'Obtener Documento Final' : 'Proceder al Pago'}
                                            onClick={handleProceedToPayment}
                                            loading={fetchingCreatePayment}
                                            icon={request?.can_skip_payment ? 'fa-file-download' : 'fa-credit-card'}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractPreview;
